import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { API_URL } from "../utils/utils";
import Swal from "sweetalert2";

// Helper function to initialize feather icons
const initFeatherIcons = () => {
  if (window.feather) {
    window.feather.replace();
  } else {
    setTimeout(initFeatherIcons, 100);
  }
};

export default function Profile() {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Form 1: Profile Update
  const [profileData, setProfileData] = useState({
    title: "",
    first_name: "",
    last_name: "",
    email: "",
  });

  // Form 2: Password Update
  const [passwordData, setPasswordData] = useState({
    new_password: "",
    confirm_password: "",
  });

  // Load user data on component mount
  useEffect(() => {
    if (user) {
      setProfileData({
        title: user.title || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
      });
    }
  }, [user]);

  // Initialize feather icons when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      initFeatherIcons();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Handle profile form changes
  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle password form changes
  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle profile update
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user || !user.id) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "User information not available",
      });
      return;
    }

    // Validate required fields based on backend validation
    if (!profileData.title || !profileData.email) {
      Swal.fire({
        icon: "warning",
        title: "Missing Required Fields",
        text: "Title and Email are required fields.",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}passenger/profile/update/${user.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          passenger_id: user.id,
          title: profileData.title,
          email: profileData.email,
          first_name: profileData.first_name || null,
          last_name: profileData.last_name || null,
        }),
      });

      console.log("Response status:", response.status);
      console.log("Response URL:", response.url);

      // Laravel returns 302 redirect with success/error in session
      // Since we're following redirects, check the final response
      if (response.redirected || response.status === 302) {
        // The backend returns redirect()->back() with session flash messages
        // For API calls, we'll treat redirect as success if status is 200 after redirect
        // Otherwise, we'll show a generic success message
        const finalStatus = response.status;
        
        if (finalStatus >= 200 && finalStatus < 300) {
          // Update user data in context
          const updatedUser = {
            ...user,
            title: profileData.title,
            first_name: profileData.first_name,
            last_name: profileData.last_name,
            email: profileData.email,
          };
          login(updatedUser);

          Swal.fire({
            icon: "success",
            title: "Profile Updated",
            text: "Your profile has been updated successfully!",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Update Failed",
            text: "An error occurred while updating your profile. Please try again.",
          });
        }
        setLoading(false);
        return;
      }

      // Try to parse JSON response if available
      let data = null;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await response.json();
          console.log("Response data:", data);
        } catch (jsonError) {
          console.warn("Could not parse JSON response:", jsonError);
        }
      }

      if (response.ok || response.status === 200) {
        // Update user data in context
        const updatedUser = {
          ...user,
          title: profileData.title,
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          email: profileData.email,
        };
        login(updatedUser);

        Swal.fire({
          icon: "success",
          title: "Profile Updated",
          text: data?.message || "Your profile has been updated successfully!",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text:
            data?.message ||
            data?.error ||
            "Failed to update profile. Please check your input and try again.",
        });
      }
    } catch (error) {
      console.error("Profile update error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.message ||
          "An error occurred while updating your profile. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle password update
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      Swal.fire({
        icon: "warning",
        title: "Password Mismatch",
        text: "New password and confirm password do not match",
      });
      return;
    }

    // Validate password length (backend requires min:8)
    if (passwordData.new_password.length < 8) {
      Swal.fire({
        icon: "warning",
        title: "Password Too Short",
        text: "Password must be at least 8 characters long",
      });
      return;
    }

    if (!user || !user.id) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "User information not available",
      });
      return;
    }

    setPasswordLoading(true);

    try {
      const response = await fetch(`${API_URL}passenger/password/update/${user.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          passenger_id: user.id,
          new_password: passwordData.new_password,
        }),
      });

      // Try to parse JSON response if available
      let data = null;

      if (response.ok || response.status === 200) {
        // Reset password form
        setPasswordData({
          new_password: "",
          confirm_password: "",
        });

        Swal.fire({
          icon: "success",
          title: "Password Updated",
          text: data?.message || "Your password has been updated successfully!",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text:
            data?.message ||
            data?.error ||
            "Failed to update password. Please try again.",
        });
      }
    } catch (error) {
      console.error("Password update error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.message ||
          "An error occurred while updating your password. Please try again.",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <>
      <div className="row profile-body">
        {/* left wrapper start */}
        <div className="col-lg-4">
          <div className="card rounded">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="card-title mb-0">About</h6>
              </div>
              <p>
                Welcome to your profile page. Here you can update your personal
                information and change your password.
              </p>
              <div className="mt-3">
                <label className="fw-bolder mb-0 text-uppercase">Title:</label>
                <p>{user?.title || "N/A"}</p>
              </div>
              <div className="mt-3">
                <label className="fw-bolder mb-0 text-uppercase">Email:</label>
                <p>{user?.email || "N/A"}</p>
              </div>
              <div className="mt-3">
                <label className="fw-bolder mb-0 text-uppercase">
                  Contact No.:
                </label>
                <p>{user?.contact_no || "N/A"}</p>
              </div>
            </div>
          </div>
        </div>
        {/* left wrapper end */}
        {/* middle wrapper start */}
        <div className="col-md-8">
          {/* Form 1: Update Profile */}
          <div className="card mb-4">
            <div className="card-body">
              <h4 className="mb-4">Update Profile</h4>
              <form
                className="forms-sample"
                onSubmit={handleProfileSubmit}
                noValidate
              >
                <div className="row">
                  <div className="col-lg-6 mb-3">
                    <label htmlFor="title" className="form-label">
                      Title
                    </label>
                    <select
                      className="form-control"
                      id="title"
                      name="title"
                      value={profileData.title}
                      onChange={handleProfileChange}
                    >
                      <option value="">Select Title</option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Ms">Ms</option>
                      <option value="Dr">Dr</option>
                    </select>
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label htmlFor="first_name" className="form-label">
                      First Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="first_name"
                      name="first_name"
                      value={profileData.first_name}
                      onChange={handleProfileChange}
                      autoComplete="off"
                      placeholder="Enter first name"
                      required
                    />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label htmlFor="last_name" className="form-label">
                      Last Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="last_name"
                      name="last_name"
                      value={profileData.last_name}
                      onChange={handleProfileChange}
                      autoComplete="off"
                      placeholder="Enter last name"
                      required
                    />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label htmlFor="email" className="form-label">
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      autoComplete="off"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                  <div className="text-center">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      <i className="link-icon" data-feather="save" />{" "}
                      {loading ? "Updating..." : "Update Profile"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Form 2: Update Password */}
          <div className="card">
            <div className="card-body">
              <h4 className="mb-4">Change Password</h4>
              <form
                className="forms-sample"
                onSubmit={handlePasswordSubmit}
                noValidate
              >
                <div className="row">
                  <div className="col-lg-6 mb-3">
                    <label htmlFor="new_password" className="form-label">
                      New Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="new_password"
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      autoComplete="off"
                      placeholder="Enter new password"
                      minLength="8"
                      required
                    />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label htmlFor="confirm_password" className="form-label">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="confirm_password"
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      autoComplete="off"
                      placeholder="Confirm new password"
                      minLength="8"
                      required
                    />
                  </div>
                  <div className="text-center">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={passwordLoading}
                    >
                      <i className="link-icon" data-feather="lock" />{" "}
                      {passwordLoading ? "Updating..." : "Update Password"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* middle wrapper end */}
      </div>
    </>
  );
}
